import { useCallback, useEffect, useState } from 'react';
import { AgxCaption, AgxLabel, AgxRow } from '@urbanx/agx-ui-components';
import { parseFloatReturnEmptyIfZero } from 'utils/parseFloatReturnEmptyIfZero';
import { FlatCommission, FlatCommissionType } from './types';
import './FlatCommission.scss';
import { parseStringToNumber } from 'utils/numbers';
import { AgxDropdown, AgxTextInput } from '@urbanx/agx.ui-components2';

interface FlatCommissionProps {
  id: string;
  onValueChanged: (value: { id: string; value: FlatCommission }) => void;
  defaultValue?: FlatCommission;
  validate?: boolean;
  required?: boolean;
  small?: boolean;
}

const options = [
  {
    value: FlatCommissionType.Percentage,
    label: 'Percentage',
  },
  {
    value: FlatCommissionType.FlatFee,
    label: 'Flat Fee',
  },
];

const getCommissionInputProps = (commissionType: FlatCommissionType) => {
  if (commissionType === FlatCommissionType.FlatFee) return { type: 'currency' as const };
  return { type: 'percent' as const, percentDecimalPlaces: 3 };
};

const FlatCommissionComponent = ({
  id,
  onValueChanged,
  defaultValue,
  validate,
  required,
}: FlatCommissionProps) => {
  const [commissionType, setCommissionType] = useState(
    defaultValue?.flatCommissionType ?? FlatCommissionType.Percentage
  );
  const [commissionRate, setCommissionRate] = useState(defaultValue?.amount);

  useEffect(() => {
    onValueChanged({
      id,
      value: {
        flatCommissionType: commissionType,
        amount: !commissionRate ? 0.0 : commissionRate,
      },
    });
  }, [commissionRate, commissionType]);

  const onCommissionTypeChange = useCallback(
    (updatedCommissionType: FlatCommissionType) => {
      if (updatedCommissionType !== commissionType) {
        setCommissionType(updatedCommissionType);
        setCommissionRate(undefined);
      }
    },
    [commissionType, setCommissionRate, setCommissionType]
  );

  return (
    <div className="agx-flatCommission">
      <AgxLabel medium>Commission Rate</AgxLabel>
      <div className="agx-flatCommission-inputs">
        <AgxDropdown
          id={id}
          options={options}
          onChange={(value) => onCommissionTypeChange(value as FlatCommissionType)}
          selected={commissionType}
          noHeader
          required
        />
        <AgxTextInput
          key={`${id}-input-${commissionType}`}
          id={`${id}-input`}
          {...getCommissionInputProps(commissionType)}
          onInputValueChange={({ value }) => {
            const parsedValue = Number(value.replaceAll(',', ''));

            if (parsedValue != undefined) setCommissionRate(parsedValue);
          }}
          noHeader
          defaultValue={commissionRate?.toString()}
          required={required}
          stretch
          errorText={
            validate && required && !parseFloatReturnEmptyIfZero(commissionRate?.toString())
              ? 'Enter commission rate'
              : undefined
          }
        />
      </div>
      <AgxRow extraClasses={'paddingTop1'}>
        <AgxCaption>Including GST</AgxCaption>
      </AgxRow>
    </div>
  );
};

export default FlatCommissionComponent;
